<template>
	<transition name="modal-fade">
		<div>
			<div v-if="isOpen" class="search-modal-overlay" @click.self="closeModal">
				<div class="search-modal">
					<!-- Modal Header -->
					<div class="search-modal-header">
						<div class="container-fluid">
							<div class="row align-items-center g-3">
								<!-- Search Box -->
								<div class="col col-lg">
									<div class="search-box">
										<i class="bi bi-search"></i>
										<input
											ref="searchInput"
											v-model="searchQuery"
											type="text"
											class="form-control"
											placeholder="Search the site..."
											@input="debouncedSearch"
											@keydown.esc="closeModal"
										/>
										<button
											v-if="searchQuery"
											@click="clearSearch"
											class="clear-search"
											aria-label="Clear search"
										>
											<i class="bi bi-x-circle"></i>
										</button>
									</div>
								</div>

								<!-- Content Type Filter -->
								<div class="col-4 col-lg-auto">
									<select
										v-model="selectedContentType"
										@change="handleSearch"
										class="form-select"
									>
										<option
											v-for="type in contentTypes"
											:key="type.value"
											:value="type.value"
										>
											{{ type.label }}
										</option>
									</select>
								</div>

								<!-- Close Button -->
								<div class="col-auto">
									<button
										@click="closeModal"
										class="btn-close-modal"
										aria-label="Close search"
									>
										<i class="bi bi-x-lg"></i>
									</button>
								</div>
							</div>
						</div>
					</div>

					<!-- Modal Body -->
					<div class="search-modal-body">
						<div class="container-fluid">
							<!-- Initial State - No Search -->
							<div v-if="!hasSearched && !searchQuery.trim()" class="search-prompt">
								<i class="bi bi-search"></i>
								<h3>Search Jaeckle</h3>
								<p>Find products, team members, pages, and more</p>
							</div>

							<!-- Minimum Characters Hint -->
							<div v-else-if="isQueryTooShort" class="search-hint">
								<i class="bi bi-search"></i>
								<h3>Keep typing</h3>
								<p>Enter at least {{ minSearchLength }} characters to search.</p>
							</div>

							<!-- Loading State -->
							<div v-else-if="loading" class="loading-container">
								<div class="spinner-border text-primary" role="status">
									<span class="visually-hidden">Loading...</span>
								</div>
							</div>

							<!-- Error State -->
							<div v-else-if="error" class="error-container">
								<div class="alert alert-danger">
									<i class="bi bi-exclamation-triangle"></i>
									{{ error }}
								</div>
							</div>

							<!-- Results -->
							<div v-else class="search-results">
								<!-- Results Info -->
								<div class="results-info">
									<p class="results-count">
										Found {{ totalResults }} result{{
											totalResults !== 1 ? 's' : ''
										}}
										<span v-if="searchQuery">for "{{ searchQuery }}"</span>
									</p>
								</div>

								<!-- No Results -->
								<div v-if="totalResults === 0 && hasSearched" class="no-results">
									<i class="bi bi-search"></i>
									<h3>No results found</h3>
									<p>Try different keywords or browse by content type</p>
								</div>

								<!-- Results Sections -->
								<div
									v-for="(section, sectionKey) in searchResults"
									:key="sectionKey"
									class="results-section"
									:class="'results-section--' + sectionKey"
									v-show="section.items && section.items.length > 0"
								>
									<!-- Section Header -->
									<div class="section-header">
										<h4>
											<i :class="section.icon"></i>
											{{ section.label }}
											<span class="section-count">({{ section.total }})</span>
										</h4>
									</div>

									<!-- Section Items -->
									<div class="section-items">
										<!-- Product Cards -->
										<template v-if="sectionKey === 'manufacturers'">
											<div
												v-for="item in section.items"
												:key="item.id"
												class="brand-card"
											>
												<div class="brand-image">
													<img
														:src="item.image"
														:alt="item.title"
														loading="lazy"
													/>
												</div>
												<div class="brand-content">
													<h4 class="brand-title">
														{{ item.title }}
													</h4>
													<div class="brand-divider"></div>
													<div
														v-if="item.meta && item.meta.taxonomies"
														class="brand-categories"
													>
														{{
															getUniqueCategories(
																item.meta.taxonomies
															)
														}}
													</div>
													<div class="brand-divider"></div>
													<a
														v-if="
															item.meta && item.meta.manufacturer_url
														"
														:href="item.meta.manufacturer_url"
														class="brand-url"
														target="_blank"
														rel="noopener noreferrer"
													>
														Visit Website
													</a>
												</div>
											</div>
										</template>

										<!-- Team Cards -->
										<template v-else-if="sectionKey === 'team'">
											<a
												v-for="item in section.items"
												:key="item.id"
												:href="getTeamResultLink(item)"
												class="result-card result-card--team"
											>
												<div class="result-card__content team-info">
													<h5 class="result-card__title team-name">
														{{ item.title }}
													</h5>
													<p
														v-if="item.meta && item.meta.job_title"
														class="team-title"
													>
														{{ item.meta.job_title }}
													</p>
													<p
														v-if="item.meta && item.meta.location"
														class="team-location"
													>
														{{ item.meta.location }}
													</p>
													<!-- <p
														v-if="item.meta && item.meta.department"
														class="team-department"
													>
														{{ item.meta.department }}
													</p> -->
												</div>
											</a>
										</template>

										<!-- News Cards -->
										<template
											v-else-if="
												sectionKey === 'news' ||
												sectionKey === 'product_news'
											"
										>
											<a
												v-for="item in section.items"
												:key="item.id"
												:href="getNewsResultLink(item)"
												class="result-card result-card--post"
											>
												<div
													v-if="item.image"
													class="result-card__thumbnail"
												>
													<img
														:src="item.image"
														:alt="item.title"
														loading="lazy"
													/>
												</div>
												<div class="result-card__content">
													<h5 class="result-card__title">
														{{ item.title }}
													</h5>
													<p
														v-if="item.meta && item.meta.excerpt"
														class="result-card__excerpt"
													>
														{{ item.meta.excerpt }}
													</p>
													<span
														v-if="item.meta && item.meta.date"
														class="result-card__date"
														>{{ item.meta.date }}</span
													>
												</div>
											</a>
										</template>

										<!-- Page Cards (default) -->
										<template v-else>
											<a
												v-for="item in section.items"
												:key="item.id"
												:href="item.link"
												class="result-card result-card--page"
											>
												<div class="result-card__content">
													<h5 class="result-card__title">
														{{ item.title }}
													</h5>
													<p
														v-if="item.meta && item.meta.excerpt"
														class="result-card__excerpt"
													>
														{{ item.meta.excerpt }}
													</p>
												</div>
											</a>
										</template>
									</div>

									<!-- Load More for Section -->
									<div v-if="section.has_more" class="section-load-more">
										<button
											@click="loadMoreSection(sectionKey)"
											:disabled="sectionLoading[sectionKey]"
											class="btn btn-outline-primary btn-sm"
										>
											<span v-if="!sectionLoading[sectionKey]">
												Show more {{ section.label.toLowerCase() }}
											</span>
											<span v-else>
												<span
													class="spinner-border spinner-border-sm me-2"
												></span>
												Loading...
											</span>
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
import { ref, watch, nextTick, computed } from 'vue';

export default {
	name: 'SearchModal',
	setup() {
		// State
		const isOpen = ref(false);
		const searchQuery = ref('');
		const loading = ref(false);
		const error = ref(null);
		const hasSearched = ref(false);
		const searchInput = ref(null);
		const selectedContentType = ref('all');
		const searchResults = ref({});
		const totalResults = ref(0);
		const sectionLoading = ref({});
		const minSearchLength = 3;
		const isQueryTooShort = computed(() => {
			const trimmedQuery = searchQuery.value.trim();
			return trimmedQuery.length > 0 && trimmedQuery.length < minSearchLength;
		});

		// Content type configuration
		const contentTypes = [
			{ value: 'all', label: 'All Content' },
			{ value: 'manufacturers', label: 'Products' },
			{ value: 'pages', label: 'Pages' },
			{ value: 'news', label: 'News' },
			{ value: 'product_news', label: 'Product News' },
			{ value: 'team', label: 'Team' },
		];

		// Debounce timer
		let searchDebounce = null;

		// Methods
		const openModal = () => {
			isOpen.value = true;
			document.body.style.overflow = 'hidden';

			nextTick(() => {
				if (searchInput.value) {
					searchInput.value.focus();
				}
			});
		};

		const closeModal = () => {
			isOpen.value = false;
			document.body.style.overflow = '';
			searchQuery.value = '';
			searchResults.value = {};
			hasSearched.value = false;
			error.value = null;
			selectedContentType.value = 'all';
			totalResults.value = 0;
			sectionLoading.value = {};
		};

		const performSearch = async (append = false, specificType = null) => {
			const normalizedQuery = searchQuery.value.trim();

			if (normalizedQuery.length < 3) {
				loading.value = false;
				error.value = null;
				hasSearched.value = false;
				searchResults.value = {};
				totalResults.value = 0;
				sectionLoading.value = {};
				return;
			}

			if (!append) {
				loading.value = true;
				if (!specificType) {
					searchResults.value = {};
				}
			}

			error.value = null;
			hasSearched.value = true;

			try {
				const contentType = specificType || selectedContentType.value;
				const page =
					append && specificType ? (searchResults.value[specificType]?.page || 1) + 1 : 1;

				const params = new URLSearchParams({
					action: 'site_wide_search',
					search: normalizedQuery,
					content_type: contentType,
					page: page,
					per_page: 5,
				});

				const response = await fetch(`${window.jaeckleData.ajaxUrl}?${params}`);

				if (!response.ok) {
					throw new Error('Failed to fetch results');
				}

				const data = await response.json();

				if (data.success) {
					if (specificType && append) {
						// Append to specific section
						const section = searchResults.value[specificType];
						section.items = [...section.items, ...data.data.items];
						section.has_more = data.data.has_more;
						section.page = data.data.page;
					} else if (data.data.sections) {
						// Full search results with sections
						searchResults.value = data.data.sections;
						totalResults.value = data.data.total;
					} else {
						// Single content type search
						const typeConfig = contentTypes.find(t => t.value === contentType);
						searchResults.value = {
							[contentType]: {
								label: typeConfig?.label || contentType,
								icon: getIconForType(contentType),
								total: data.data.items.length,
								has_more: data.data.has_more,
								items: data.data.items,
								page: data.data.page,
							},
						};
						totalResults.value = data.data.items.length;
					}
				} else {
					throw new Error(data.data?.message || 'Search failed');
				}
			} catch (err) {
				error.value = err.message;
				console.error('Error searching:', err);
			} finally {
				loading.value = false;
			}
		};

		const getIconForType = type => {
			const icons = {
				manufacturers: 'bi-box',
				pages: 'bi-file-text',
				news: 'bi-newspaper',
				product_news: 'bi-newspaper',
				team: 'bi-people',
			};
			return icons[type] || 'bi-search';
		};

		const debouncedSearch = () => {
			clearTimeout(searchDebounce);
			searchDebounce = setTimeout(() => {
				performSearch(false);
			}, 500);
		};

		const handleSearch = () => {
			performSearch(false);
		};

		const clearSearch = () => {
			searchQuery.value = '';
			searchResults.value = {};
			hasSearched.value = false;
			totalResults.value = 0;
			selectedContentType.value = 'all';
			nextTick(() => {
				if (searchInput.value) {
					searchInput.value.focus();
				}
			});
		};

		const loadMoreSection = async sectionKey => {
			sectionLoading.value[sectionKey] = true;
			await performSearch(true, sectionKey);
			sectionLoading.value[sectionKey] = false;
		};

		const getTeamResultLink = item => {
			const fallbackUrl = item?.link || window.jaeckleData?.siteUrl || '/';
			const teamPageUrl = item?.meta?.team_page_url || fallbackUrl;

			try {
				const url = new URL(teamPageUrl, window.location.origin);

				if (searchQuery.value.trim()) {
					url.searchParams.set('search', searchQuery.value.trim());
				}

				return url.toString();
			} catch (e) {
				return fallbackUrl;
			}
		};

		const getNewsResultLink = item => {
			const fallbackUrl = item?.link || window.jaeckleData?.siteUrl || '/';
			const listingPageUrl = item?.meta?.listing_page_url || fallbackUrl;

			try {
				const url = new URL(listingPageUrl, window.location.origin);

				if (searchQuery.value.trim()) {
					url.searchParams.set('search', searchQuery.value.trim());
				}

				return url.toString();
			} catch (e) {
				return fallbackUrl;
			}
		};

		const getUniqueCategories = taxonomies => {
			const allTerms = [];
			Object.values(taxonomies).forEach(terms => {
				allTerms.push(...terms);
			});
			// Remove duplicates and join
			return [...new Set(allTerms)].join(', ');
		};

		// Watch for escape key globally when modal is open
		watch(isOpen, newValue => {
			if (newValue) {
				const handleEscape = e => {
					if (e.key === 'Escape') {
						closeModal();
					}
				};
				document.addEventListener('keydown', handleEscape);

				return () => {
					document.removeEventListener('keydown', handleEscape);
				};
			}
		});

		return {
			isOpen,
			searchQuery,
			loading,
			error,
			hasSearched,
			searchInput,
			selectedContentType,
			searchResults,
			totalResults,
			sectionLoading,
			minSearchLength,
			isQueryTooShort,
			contentTypes,
			openModal,
			closeModal,
			performSearch,
			debouncedSearch,
			handleSearch,
			clearSearch,
			loadMoreSection,
			getTeamResultLink,
			getNewsResultLink,
			getUniqueCategories,
		};
	},
};
</script>

<style scoped>
/* Component-specific styles - main styles in search-modal.scss */
</style>
